import { Temporal } from "temporal-polyfill";

import type { Order } from "~/v2/api/rest/account";
import {
  getOrderCancellationDisabledReason,
  getOrderReplacementEligibility,
} from "./OrderReplacementDialog.utils";
import type {
  OrdersWidgetBadgeColor,
  OrdersWidgetFilters,
  OrdersWidgetLeg,
  OrdersWidgetRow,
  OrdersWidgetStatusFilter,
  OrdersWidgetStatusSelection,
  OrdersWidgetStatusTab,
  OrdersWidgetTimeWindow,
} from "./OrdersWidget.types";
import {
  isFilterableOmsStatus,
  OMS_ORDER_STATUSES,
  ORDERS_STATUS_TABS,
} from "./OrdersWidget.types";
import { ORDERS_TIME_ZONE } from "./ordersTimeWindow";

export const ORDERS_UNAVAILABLE_VALUE = "—";

export const isOrdersSortUnavailable = (value: string): boolean =>
  value === ORDERS_UNAVAILABLE_VALUE || value.trim() === "";

const compareOrdersUnavailableLast = (
  aMissing: boolean,
  bMissing: boolean,
  comparePresent: () => number,
  descending: boolean,
): number => {
  if (aMissing && bMissing) {
    return 0;
  }
  if (aMissing) {
    return descending ? -1 : 1;
  }
  if (bMissing) {
    return descending ? 1 : -1;
  }
  return comparePresent();
};

export const compareOrdersTextValues = (
  left: string,
  right: string,
  descending: boolean = false,
): number =>
  compareOrdersUnavailableLast(
    isOrdersSortUnavailable(left),
    isOrdersSortUnavailable(right),
    () => left.localeCompare(right, undefined, { sensitivity: "base" }),
    descending,
  );

export const parseOrdersNumericSortValue = (
  value: string,
): number | undefined => {
  if (isOrdersSortUnavailable(value)) {
    return undefined;
  }

  const target = value.includes("@")
    ? value.slice(value.lastIndexOf("@") + 1)
    : value;
  const normalized = target.replace(/,/g, "").replace(/%/g, "").trim();
  if (isOrdersSortUnavailable(normalized)) {
    return undefined;
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const compareOrdersNumericValues = (
  left: string,
  right: string,
  descending: boolean = false,
): number => {
  const leftValue = parseOrdersNumericSortValue(left);
  const rightValue = parseOrdersNumericSortValue(right);
  return compareOrdersUnavailableLast(
    leftValue === undefined,
    rightValue === undefined,
    () => (leftValue ?? 0) - (rightValue ?? 0),
    descending,
  );
};

export const compareOrdersTimestampValues = (
  left: string,
  right: string,
  descending: boolean = false,
): number => {
  const leftTime = Date.parse(left);
  const rightTime = Date.parse(right);
  return compareOrdersUnavailableLast(
    !Number.isFinite(leftTime),
    !Number.isFinite(rightTime),
    () => leftTime - rightTime,
    descending,
  );
};

const quantityFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 8,
});

const amountFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 8,
});

const timestampFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
  timeZone: ORDERS_TIME_ZONE,
});

export const isOrdersStatusTab = (
  status: string,
): status is OrdersWidgetStatusTab =>
  (ORDERS_STATUS_TABS as readonly string[]).includes(status);

const isOmsOrderStatus = (
  status: string,
): status is (typeof OMS_ORDER_STATUSES)[number] =>
  (OMS_ORDER_STATUSES as readonly string[]).includes(status);

const STATUS_FILTER_RANK = new Map<string, number>(
  [
    ...ORDERS_STATUS_TABS,
    ...OMS_ORDER_STATUSES.filter((status) => !isOrdersStatusTab(status)),
  ].map((status, index) => [status, index]),
);

export const sortOrdersStatusFilters = (
  statuses: readonly OrdersWidgetStatusFilter[],
): OrdersWidgetStatusFilter[] =>
  [...new Set(statuses)].sort((first, second) => {
    const firstRank = STATUS_FILTER_RANK.get(first) ?? Number.MAX_SAFE_INTEGER;
    const secondRank =
      STATUS_FILTER_RANK.get(second) ?? Number.MAX_SAFE_INTEGER;
    return firstRank - secondRank || first.localeCompare(second);
  });

export const normalizeOrdersStatusFilters = (
  status:
    | OrdersWidgetStatusFilter
    | readonly OrdersWidgetStatusFilter[]
    | undefined,
): OrdersWidgetStatusSelection => {
  const values = (
    Array.isArray(status) ? status : status ? [status] : []
  ).filter(
    (value): value is OrdersWidgetStatusFilter =>
      value === "all" ||
      value === "open" ||
      value === "closed" ||
      isOmsOrderStatus(value),
  );
  if (values.length === 0 || values.includes("all")) {
    return ["all"];
  }
  return sortOrdersStatusFilters(values);
};

export const getSelectedStatusTabs = (
  statuses: OrdersWidgetStatusSelection,
): readonly OrdersWidgetStatusTab[] => {
  const normalized = normalizeOrdersStatusFilters(statuses);
  if (normalized.includes("all")) {
    return ["all"];
  }
  return ORDERS_STATUS_TABS.filter(
    (tab) => tab !== "all" && normalized.includes(tab),
  );
};

export const getSelectedStatusTab = (
  statuses: OrdersWidgetStatusSelection,
): OrdersWidgetStatusTab => {
  const tabs = getSelectedStatusTabs(statuses);
  return tabs.find((tab) => tab !== "filled") ?? tabs[0] ?? "all";
};

export const getSelectedOmsStatuses = (
  statuses: OrdersWidgetStatusSelection,
): readonly (typeof OMS_ORDER_STATUSES)[number][] =>
  normalizeOrdersStatusFilters(statuses).filter(isOmsOrderStatus);

export const getSelectedFilterableOmsStatuses = (
  statuses: OrdersWidgetStatusSelection,
): readonly (typeof OMS_ORDER_STATUSES)[number][] =>
  getSelectedOmsStatuses(statuses).filter(isFilterableOmsStatus);

export const setOrdersStatusTab = (
  current: OrdersWidgetStatusSelection,
  tab: OrdersWidgetStatusTab,
): OrdersWidgetStatusSelection => {
  if (tab === "all") {
    return ["all"];
  }
  const currentTab = getSelectedStatusTab(current);
  const concreteOmsStatuses = getSelectedOmsStatuses(current).filter(
    (status) => status !== currentTab,
  );
  return normalizeOrdersStatusFilters([tab, ...concreteOmsStatuses]);
};

export const toggleOrdersOmsStatus = (
  current: OrdersWidgetStatusSelection,
  status: (typeof OMS_ORDER_STATUSES)[number],
  selected: boolean,
): OrdersWidgetStatusSelection => {
  const withoutStatus = normalizeOrdersStatusFilters(current).filter(
    (value) => value !== "all" && value !== status,
  );
  return normalizeOrdersStatusFilters(
    selected ? [...withoutStatus, status] : withoutStatus,
  );
};

export const clearOrdersOmsStatuses = (
  current: OrdersWidgetStatusSelection,
): OrdersWidgetStatusSelection => [getSelectedStatusTab(current)];

export const toAtdOrdersStatusParam = (
  statuses: OrdersWidgetStatusSelection,
): string => {
  const normalized = normalizeOrdersStatusFilters(statuses);
  return normalized.includes("all") ? "all" : normalized.join(",");
};

const dateLabelFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

const finiteNumber = (value: unknown): number | undefined => {
  if (typeof value !== "number" && typeof value !== "string") {
    return undefined;
  }

  if (typeof value === "string" && value.trim() === "") {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const displayText = (value: unknown): string =>
  typeof value === "string" && value.trim()
    ? value.trim()
    : ORDERS_UNAVAILABLE_VALUE;

export const formatOrderLabel = (value: unknown): string => {
  if (typeof value !== "string" || !value.trim()) {
    return ORDERS_UNAVAILABLE_VALUE;
  }

  return value
    .trim()
    .replaceAll("_", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const ORDER_MARKET_BY_ASSET_CLASS: Record<string, string> = {
  us_equity: "US",
  us_option: "US",
  crypto: "Crypto",
};

export const deriveOrderMarket = (assetClass: unknown): string => {
  if (typeof assetClass !== "string" || !assetClass.trim()) {
    return ORDERS_UNAVAILABLE_VALUE;
  }

  const normalizedAssetClass = assetClass.trim().toLowerCase();
  return (
    ORDER_MARKET_BY_ASSET_CLASS[normalizedAssetClass] ??
    formatOrderLabel(normalizedAssetClass)
  );
};

export const formatOrderQuantity = (value: unknown): string => {
  const quantity = finiteNumber(value);
  return quantity === undefined
    ? ORDERS_UNAVAILABLE_VALUE
    : quantityFormatter.format(quantity);
};

export const formatOrderPrice = (value: unknown): string => {
  const price = finiteNumber(value);
  return price === undefined
    ? ORDERS_UNAVAILABLE_VALUE
    : amountFormatter.format(price);
};

export const formatOrderNotional = (value: unknown): string => {
  const notional = finiteNumber(value);
  return notional === undefined
    ? ORDERS_UNAVAILABLE_VALUE
    : amountFormatter.format(notional);
};

export const formatOrderPercentage = (value: unknown): string => {
  const percentage = finiteNumber(value);
  return percentage === undefined
    ? ORDERS_UNAVAILABLE_VALUE
    : `${quantityFormatter.format(percentage)}%`;
};

export const formatOrderDecimal = (value: unknown): string => {
  const number = finiteNumber(value);
  return number === undefined
    ? ORDERS_UNAVAILABLE_VALUE
    : quantityFormatter.format(number);
};

export const formatOrderFilledAtAveragePrice = (
  filledQuantity: unknown,
  filledAveragePrice: unknown,
): string => {
  const quantity = finiteNumber(filledQuantity);
  const averagePrice = finiteNumber(filledAveragePrice);

  if (quantity === undefined && averagePrice === undefined) {
    return ORDERS_UNAVAILABLE_VALUE;
  }
  if (quantity === undefined) {
    return `${ORDERS_UNAVAILABLE_VALUE} @ ${amountFormatter.format(
      averagePrice ?? 0,
    )}`;
  }
  if (averagePrice === undefined) {
    return `${quantityFormatter.format(quantity)} @ ${ORDERS_UNAVAILABLE_VALUE}`;
  }

  return `${quantityFormatter.format(quantity)} @ ${amountFormatter.format(
    averagePrice,
  )}`;
};

export const formatOrderTimestamp = (value: unknown): string => {
  if (typeof value !== "string" || !value.trim()) {
    return ORDERS_UNAVAILABLE_VALUE;
  }

  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) {
    return ORDERS_UNAVAILABLE_VALUE;
  }

  const parts = timestampFormatter.formatToParts(timestamp);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value ?? "";

  return `${part("month")} ${part("day")} · ${part("hour")}:${part(
    "minute",
  )}:${part("second")} ET`;
};

const rawOrderTimestamp = (value: unknown): string =>
  typeof value === "string" ? value : "";

export const MULTI_LEG_OPTIONS_ORDER_NAME = "Multi-leg options order";

const OCC_SYMBOL_PATTERN = /^([A-Z]{1,6})(\d{6})([CP])(\d{8})$/;

export const isOccOptionSymbol = (value: unknown): boolean => {
  if (typeof value !== "string" || !value.trim()) {
    return false;
  }
  return OCC_SYMBOL_PATTERN.test(value.trim().toUpperCase());
};

const getOccRoot = (value: unknown): string | undefined => {
  if (typeof value !== "string" || !value.trim()) {
    return undefined;
  }
  return OCC_SYMBOL_PATTERN.exec(value.trim().toUpperCase())?.[1];
};

export const getOrderUnderlyingSymbol = (order: Order): string => {
  const candidates = [
    order.symbol,
    ...(Array.isArray(order.legs) ? order.legs.map((leg) => leg.symbol) : []),
  ];
  for (const candidate of candidates) {
    if (typeof candidate !== "string" || !candidate.trim()) {
      continue;
    }
    const root = getOccRoot(candidate);
    if (root) {
      return root;
    }
    return candidate.trim();
  }
  return ORDERS_UNAVAILABLE_VALUE;
};

const optionExpiryLongFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "2-digit",
  timeZone: "UTC",
});

export const formatPrettyOrderSymbol = (value: unknown): string => {
  if (typeof value !== "string" || !value.trim()) {
    return ORDERS_UNAVAILABLE_VALUE;
  }

  const symbol = value.trim().toUpperCase();
  const match = OCC_SYMBOL_PATTERN.exec(symbol);
  if (!match) {
    return value.trim();
  }

  const [, root, yymmdd, right, strikeRaw] = match;
  const month = yymmdd.slice(2, 4);
  const day = yymmdd.slice(4, 6);
  const strike = Number.parseInt(strikeRaw, 10) / 1000;
  const strikeLabel = Number.isInteger(strike)
    ? String(strike)
    : String(strike);

  return `${root} ${strikeLabel}${right} ${month}/${day}`;
};

/** Long option label, e.g. `AAPL Dec 13, 24 $250 Call`. Non-OCC stays verbatim. */
export const formatLongOrderOptionSymbol = (value: unknown): string => {
  if (typeof value !== "string" || !value.trim()) {
    return ORDERS_UNAVAILABLE_VALUE;
  }

  const symbol = value.trim().toUpperCase();
  const match = OCC_SYMBOL_PATTERN.exec(symbol);
  if (!match) {
    return value.trim();
  }

  const [, root, yymmdd, right, strikeRaw] = match;
  const year = 2000 + Number.parseInt(yymmdd.slice(0, 2), 10);
  const month = Number.parseInt(yymmdd.slice(2, 4), 10);
  const day = Number.parseInt(yymmdd.slice(4, 6), 10);
  const expiry = new Date(Date.UTC(year, month - 1, day));
  if (
    expiry.getUTCFullYear() !== year ||
    expiry.getUTCMonth() !== month - 1 ||
    expiry.getUTCDate() !== day
  ) {
    return value.trim();
  }
  const strike = Number.parseInt(strikeRaw, 10) / 1000;
  const strikeLabel = Number.isInteger(strike)
    ? String(strike)
    : String(strike);
  const kind = right === "P" ? "Put" : "Call";

  return `${root} ${optionExpiryLongFormatter.format(expiry)} $${strikeLabel} ${kind}`;
};

export const collectOrderAssetNameSymbols = (
  orders: readonly Order[],
): string[] => {
  const symbols = new Set<string>();
  for (const order of orders) {
    const candidates = [
      order.symbol,
      ...(Array.isArray(order.legs) ? order.legs.map((leg) => leg.symbol) : []),
    ];
    for (const candidate of candidates) {
      if (
        typeof candidate !== "string" ||
        !candidate.trim() ||
        isOccOptionSymbol(candidate)
      ) {
        continue;
      }
      symbols.add(candidate.trim());
    }
  }
  return [...symbols].sort();
};

export const resolveOrderDisplayName = (
  row: {
    isLegRow: boolean;
    legCount: number;
    rawSymbol: string;
  },
  assetNames: ReadonlyMap<string, string> = new Map(),
): string => {
  if (row.legCount > 1 && !row.isLegRow) {
    return MULTI_LEG_OPTIONS_ORDER_NAME;
  }
  const rawSymbol = row.rawSymbol.trim();
  if (!rawSymbol || rawSymbol === ORDERS_UNAVAILABLE_VALUE) {
    return ORDERS_UNAVAILABLE_VALUE;
  }
  if (isOccOptionSymbol(rawSymbol)) {
    const contractName = formatLongOrderOptionSymbol(rawSymbol);
    return contractName === rawSymbol.toUpperCase() ||
      contractName === rawSymbol
      ? ORDERS_UNAVAILABLE_VALUE
      : contractName;
  }
  return (
    assetNames.get(rawSymbol) ||
    assetNames.get(rawSymbol.toUpperCase()) ||
    ORDERS_UNAVAILABLE_VALUE
  );
};

export const formatOrderSideLabel = (side: unknown): string => {
  const normalizedSide =
    typeof side === "string" ? side.trim().toLowerCase() : "";
  if (normalizedSide === "buy") {
    return "buy";
  }
  if (normalizedSide === "sell" || normalizedSide === "sell_short") {
    return "sell";
  }
  return formatOrderLabel(side).toLowerCase();
};

export const getOrderLegCount = (order: Order): number => {
  const legs = order.legs;
  return Array.isArray(legs) ? legs.filter(Boolean).length : 0;
};

const formatOrderLegFilledLabel = (leg: Order): string => {
  const filledQuantity = formatOrderQuantity(leg.filled_qty);
  return finiteNumber(leg.filled_avg_price) === undefined
    ? filledQuantity
    : formatOrderFilledAtAveragePrice(leg.filled_qty, leg.filled_avg_price);
};

export const mapOrderLegs = (order: Order): OrdersWidgetLeg[] => {
  if (!Array.isArray(order.legs)) {
    return [];
  }

  return order.legs.filter(Boolean).map((leg) => {
    const sideLabel = formatOrderLabel(formatOrderSideLabel(leg.side));
    return {
      id: displayText(leg.id),
      sideLabel,
      sideColor: getOrderSideBadgeColor(leg.side),
      description: formatLongOrderOptionSymbol(leg.symbol),
      ratio: formatOrderQuantity(leg.ratio_qty),
      quantity: formatOrderQuantity(leg.qty),
      filledLabel: formatOrderLegFilledLabel(leg),
      positionIntent: formatOrderPositionIntent(leg.position_intent),
    };
  });
};

const REPLACEABLE_ORDER_STATUSES = new Set(["new", "partially_filled"]);

export const isOrderReplaceable = (order: Order): boolean => {
  const status =
    typeof order.status === "string" ? order.status.trim().toLowerCase() : "";
  const orderType = (order.order_type || order.type || "").trim().toLowerCase();
  return (
    orderType === "limit" &&
    REPLACEABLE_ORDER_STATUSES.has(status) &&
    Boolean(order.id)
  );
};

export const formatOrderExtendedHours = (value: unknown): string =>
  typeof value === "boolean"
    ? value
      ? "Yes"
      : "No"
    : ORDERS_UNAVAILABLE_VALUE;

export const formatOrderTimeInForce = (value: unknown): string => {
  if (typeof value !== "string" || !value.trim()) {
    return ORDERS_UNAVAILABLE_VALUE;
  }

  const normalizedValue = value.trim().toLowerCase();
  if (["gtc", "opg", "cls", "ioc", "fok"].includes(normalizedValue)) {
    return normalizedValue.toUpperCase();
  }
  return formatOrderLabel(normalizedValue);
};

export const formatOrderPositionIntent = (value: unknown): string => {
  const normalizedValue =
    typeof value === "string" ? value.trim().toLowerCase() : "";
  const positionIntentLabels: Record<string, string> = {
    buy_to_open: "Buy to Open",
    buy_to_close: "Buy to Close",
    sell_to_open: "Sell to Open",
    sell_to_close: "Sell to Close",
  };

  return positionIntentLabels[normalizedValue] ?? formatOrderLabel(value);
};

export {
  getRollingOrdersAfter,
  isOrdersRollingTimeWindow,
} from "./ordersTimeWindow";

export const normalizeOrdersDate = (
  value: string,
  boundary: "start" | "end",
): string | undefined => {
  const dateValue = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return undefined;
  }

  try {
    const date = Temporal.PlainDate.from(dateValue);
    const boundaryDate = boundary === "end" ? date.add({ days: 1 }) : date;
    const instant = boundaryDate
      .toZonedDateTime({
        timeZone: ORDERS_TIME_ZONE,
        plainTime: Temporal.PlainTime.from("00:00"),
      })
      .toInstant();

    return boundary === "end"
      ? instant.subtract({ milliseconds: 1 }).toString()
      : instant.toString();
  } catch {
    return undefined;
  }
};

export const getOrdersFilterDateParams = (
  filters: OrdersWidgetFilters,
): { after: string | undefined; until: string | undefined } =>
  filters.timeWindow === "custom"
    ? {
        after: normalizeOrdersDate(filters.fromDate, "start"),
        until: normalizeOrdersDate(filters.toDate, "end"),
      }
    : { after: undefined, until: undefined };

export const createDefaultOrdersFilters = (
  status:
    | OrdersWidgetStatusFilter
    | readonly OrdersWidgetStatusFilter[] = "all",
): OrdersWidgetFilters => ({
  symbol: "",
  status: normalizeOrdersStatusFilters(status),
  orderType: "all",
  assetClass: "all",
  timeWindow: "all",
  fromDate: "",
  toDate: "",
  direction: "desc",
});

export const normalizeOrdersFiltersForApplication = (
  filters: OrdersWidgetFilters,
): OrdersWidgetFilters => {
  const normalizedFilters = {
    ...filters,
    symbol: filters.symbol.trim().toUpperCase(),
    status: normalizeOrdersStatusFilters(filters.status),
  };

  return filters.timeWindow === "custom"
    ? normalizedFilters
    : { ...normalizedFilters, fromDate: "", toDate: "" };
};

const statusFiltersAreEqual = (
  first: OrdersWidgetStatusSelection,
  second: OrdersWidgetStatusSelection,
): boolean => {
  const firstStatuses = normalizeOrdersStatusFilters(first);
  const secondStatuses = normalizeOrdersStatusFilters(second);
  return (
    firstStatuses.length === secondStatuses.length &&
    firstStatuses.every((status, index) => status === secondStatuses[index])
  );
};

const ordersFiltersAreEqual = (
  first: OrdersWidgetFilters,
  second: OrdersWidgetFilters,
): boolean =>
  first.symbol === second.symbol &&
  statusFiltersAreEqual(first.status, second.status) &&
  first.orderType === second.orderType &&
  first.assetClass === second.assetClass &&
  first.timeWindow === second.timeWindow &&
  first.fromDate === second.fromDate &&
  first.toDate === second.toDate &&
  first.direction === second.direction;

export const isOrdersFiltersDirty = (
  draftFilters: OrdersWidgetFilters,
  appliedFilters: OrdersWidgetFilters,
): boolean =>
  !ordersFiltersAreEqual(
    normalizeOrdersFiltersForApplication(draftFilters),
    normalizeOrdersFiltersForApplication(appliedFilters),
  );

export const hasAppliedOrdersFilters = (
  filters: OrdersWidgetFilters,
  defaultStatus:
    | OrdersWidgetStatusFilter
    | readonly OrdersWidgetStatusFilter[] = "all",
): boolean =>
  !ordersFiltersAreEqual(
    normalizeOrdersFiltersForApplication(filters),
    createDefaultOrdersFilters(defaultStatus),
  );

export const getActiveAdvancedFilterCount = (
  filters: OrdersWidgetFilters,
): number =>
  Number(filters.orderType !== "all") +
  Number(filters.assetClass !== "all") +
  Number(
    getSelectedFilterableOmsStatuses(filters.status).some(
      (status) => !isOrdersStatusTab(status),
    ),
  );

export const getOrdersFiltersValidationError = (
  filters: OrdersWidgetFilters,
): string | undefined => {
  if (filters.timeWindow !== "custom") {
    return undefined;
  }

  if (!filters.fromDate || !filters.toDate) {
    return "Choose both From and To dates.";
  }

  const from = normalizeOrdersDate(filters.fromDate, "start");
  const to = normalizeOrdersDate(filters.toDate, "end");
  if (!from || !to) {
    return "Enter valid From and To dates.";
  }

  return Date.parse(from) > Date.parse(to)
    ? "From date must be on or before To date."
    : undefined;
};

const TIME_WINDOW_LABELS: Record<
  Exclude<OrdersWidgetTimeWindow, "custom">,
  string
> = {
  all: "All",
  last24Hours: "Last 24 hours",
  last3Days: "Last 3 days",
  last7Days: "Last 7 days",
  last1Month: "Last 1 month",
};

const TIME_WINDOW_SHORT_LABELS: Record<OrdersWidgetTimeWindow, string> = {
  all: "All",
  last24Hours: "24h",
  last3Days: "3d",
  last7Days: "7d",
  last1Month: "1m",
  custom: "Custom",
};

export const formatOrdersTimeWindowShort = (
  timeWindow: OrdersWidgetTimeWindow,
): string => TIME_WINDOW_SHORT_LABELS[timeWindow];

export const formatOrdersTimeWindow = (
  filters: OrdersWidgetFilters,
): string => {
  if (filters.timeWindow !== "custom") {
    return TIME_WINDOW_LABELS[filters.timeWindow];
  }

  const from = Date.parse(`${filters.fromDate}T00:00:00Z`);
  const to = Date.parse(`${filters.toDate}T00:00:00Z`);
  if (!Number.isFinite(from) || !Number.isFinite(to)) {
    return "Custom range";
  }

  return `${dateLabelFormatter.format(from)} – ${dateLabelFormatter.format(to)}`;
};

export const getOrderSideBadgeColor = (
  side: unknown,
): OrdersWidgetBadgeColor => {
  const normalizedSide =
    typeof side === "string" ? side.trim().toLowerCase() : "";
  if (normalizedSide === "buy") {
    return "green";
  }
  if (normalizedSide === "sell" || normalizedSide === "sell_short") {
    return "red";
  }
  return "gray";
};

/**
 * Temporary display-only tones for scanning raw Broker API statuses. This does
 * not collapse statuses into the product's future six-state filter model.
 */
export const getOrderStatusBadgeColor = (
  status: unknown,
): OrdersWidgetBadgeColor => {
  const normalizedStatus =
    typeof status === "string" ? status.trim().toLowerCase() : "";

  if (normalizedStatus === "filled") {
    return "green";
  }
  if (normalizedStatus === "rejected") {
    return "red";
  }
  if (normalizedStatus === "partially_filled") {
    return "orange";
  }
  if (
    [
      "accepted",
      "accepted_for_bidding",
      "held",
      "new",
      "open",
      "pending_cancel",
      "pending_new",
      "pending_replace",
      "pending_review",
      "stopped",
      "suspended",
    ].includes(normalizedStatus)
  ) {
    return "yellow";
  }
  return "gray";
};

const CANCELLABLE_ORDER_STATUSES = new Set([
  "accepted",
  "accepted_for_bidding",
  "held",
  "new",
  "open",
  "partially_filled",
  "pending_cancel",
  "pending_new",
  "pending_replace",
  "pending_review",
  "stopped",
  "suspended",
]);

export const isOrderCancellable = (status: unknown): boolean =>
  typeof status === "string" &&
  CANCELLABLE_ORDER_STATUSES.has(status.trim().toLowerCase());

interface MapOrderToRowOptions {
  allowCancel?: boolean;
  allowEdit?: boolean;
  isEditing?: boolean;
  isCancelling?: boolean;
  isLegRow?: boolean;
  parentOrderId?: string;
  submittedAtValue?: string;
  assetNames?: ReadonlyMap<string, string>;
}

export const mapOrderToRow = (
  order: Order,
  options: MapOrderToRowOptions = {},
): OrdersWidgetRow => {
  const legCount = getOrderLegCount(order);
  const isMultiLeg = legCount > 1;
  const rawSymbol = isMultiLeg
    ? getOrderUnderlyingSymbol(order)
    : displayText(order.symbol);
  const orderSideLabel = formatOrderSideLabel(order.side);
  const sideLabel =
    isMultiLeg && orderSideLabel === ORDERS_UNAVAILABLE_VALUE
      ? "multi"
      : orderSideLabel;
  const replacementEligibility = getOrderReplacementEligibility(order);
  const cancellationDisabledReason = getOrderCancellationDisabledReason(order);
  const canEdit = options.allowEdit === true && replacementEligibility.eligible;

  return {
    id: displayText(order.id),
    action: "Edit / Cancel",
    canEdit,
    editDisabledReason: replacementEligibility.eligible
      ? undefined
      : replacementEligibility.reason,
    canCancel:
      options.allowCancel === true && cancellationDisabledReason === undefined,
    cancelDisabledReason: cancellationDisabledReason,
    canReplace: canEdit,
    isEditing: options.isEditing === true,
    isCancelling: options.isCancelling === true,
    isLegRow: options.isLegRow === true,
    ratioLabel: "",
    parentOrderId: options.parentOrderId ?? displayText(order.id),
    submittedAtValue:
      options.submittedAtValue ?? rawOrderTimestamp(order.submitted_at),
    createdAtValue: rawOrderTimestamp(order.created_at),
    startTimeValue: rawOrderTimestamp(order.advanced_instructions?.start_time),
    endTimeValue: rawOrderTimestamp(order.advanced_instructions?.end_time),
    createdAt: formatOrderTimestamp(order.created_at),
    submittedAt: formatOrderTimestamp(order.submitted_at),
    legCount,
    legs: isMultiLeg ? mapOrderLegs(order) : [],
    status: formatOrderLabel(order.status),
    statusColor: getOrderStatusBadgeColor(order.status),
    symbol: rawSymbol,
    rawSymbol,
    name: resolveOrderDisplayName(
      {
        isLegRow: options.isLegRow === true,
        legCount,
        rawSymbol,
      },
      options.assetNames,
    ),
    side: sideLabel,
    sideColor: getOrderSideBadgeColor(sideLabel),
    quantity: formatOrderQuantity(order.qty),
    filledQuantity: formatOrderQuantity(order.filled_qty),
    orderType: formatOrderLabel(order.order_type || order.type),
    limitPrice: formatOrderPrice(order.limit_price),
    stopPrice: formatOrderPrice(order.stop_price),
    trailPrice: formatOrderPrice(order.trail_price),
    trailPercent: formatOrderPercentage(order.trail_percent),
    extendedHours: formatOrderExtendedHours(order.extended_hours),
    clientOrderId: displayText(order.client_order_id),
    orderClass: formatOrderLabel(order.order_class),
    algorithm: displayText(order.advanced_instructions?.algorithm),
    // blotter destination is oms-assigned top-level destination (c18); ai destination is not the blotter column
    destination: displayText(order.destination),
    startTime: formatOrderTimestamp(order.advanced_instructions?.start_time),
    endTime: formatOrderTimestamp(order.advanced_instructions?.end_time),
    maxPercentage: formatOrderDecimal(
      order.advanced_instructions?.max_percentage,
    ),
    notional: formatOrderNotional(order.notional),
    timeInForce: formatOrderTimeInForce(order.time_in_force),
    filledAtAveragePrice: formatOrderFilledAtAveragePrice(
      order.filled_qty,
      order.filled_avg_price,
    ),
    source: formatOrderLabel(order.source),
    positionIntent: formatOrderPositionIntent(order.position_intent),
    markets: deriveOrderMarket(order.asset_class),
    currency: ORDERS_UNAVAILABLE_VALUE,
  };
};

/** Always-visible parent + linked child rows for a multi-leg order. */
export const mapOrderToLinkedRows = (
  order: Order,
  options: MapOrderToRowOptions = {},
): OrdersWidgetRow[] => {
  const parent = mapOrderToRow(order, options);
  if (parent.legCount <= 1 || !Array.isArray(order.legs)) {
    return [parent];
  }

  const childRows = order.legs.filter(Boolean).map((leg) => {
    const sideLabel = formatOrderSideLabel(leg.side);
    const status = leg.status ?? order.status;
    return {
      ...parent,
      id: `${parent.id}:${displayText(leg.id)}`,
      isLegRow: true,
      parentOrderId: parent.id,
      canEdit: false,
      canCancel: false,
      canReplace: false,
      editDisabledReason: undefined,
      cancelDisabledReason: undefined,
      isEditing: false,
      isCancelling: false,
      legCount: 0,
      legs: [],
      ratioLabel: formatOrderQuantity(leg.ratio_qty),
      symbol: displayText(leg.symbol),
      rawSymbol: displayText(leg.symbol),
      name: resolveOrderDisplayName(
        {
          isLegRow: true,
          legCount: 0,
          rawSymbol: displayText(leg.symbol),
        },
        options.assetNames,
      ),
      side: sideLabel,
      sideColor: getOrderSideBadgeColor(sideLabel),
      status: formatOrderLabel(status),
      statusColor: getOrderStatusBadgeColor(status),
      quantity: formatOrderQuantity(leg.qty),
      filledQuantity: formatOrderQuantity(leg.filled_qty),
      limitPrice: formatOrderPrice(leg.limit_price),
      stopPrice: ORDERS_UNAVAILABLE_VALUE,
      trailPrice: ORDERS_UNAVAILABLE_VALUE,
      trailPercent: ORDERS_UNAVAILABLE_VALUE,
      positionIntent: formatOrderPositionIntent(leg.position_intent),
      filledAtAveragePrice: formatOrderFilledAtAveragePrice(
        leg.filled_qty,
        leg.filled_avg_price,
      ),
    };
  });

  return [parent, ...childRows];
};
